// All German copy + option lists for the "big" Schadenmeldung wizard.
// Text is taken from "Schadenmeldung Stand 2026 05 27" (business-logic docs).

export interface Option {
  value: string;
  label: string;
  helper?: string;
}

export const YES_NO: Option[] = [
  { value: "ja", label: "Ja" },
  { value: "nein", label: "Nein" },
];

export const YES_NO_UNSURE: Option[] = [
  { value: "nein", label: "Nein" },
  { value: "ja", label: "Ja" },
  { value: "unsicher", label: "Ich bin nicht sicher" },
];

export const YES_NO_UNKNOWN: Option[] = [
  { value: "ja", label: "Ja" },
  { value: "nein", label: "Nein" },
  { value: "unbekannt", label: "Ich weiß es nicht" },
];

export const BIG_COPY = {
  brandLine: "Schaden melden",
  hero: { eyebrow: "Schadenmeldung" },
  nav: {
    back: "Zurück",
    next: "Weiter",
    submit: "Schaden melden",
    edit: "Bearbeiten",
    again: "Neue Meldung",
  },

  // short labels for the step indicator
  steps: {
    type: "Schadenart",
    geb_melder: "Melder",
    geb_when: "Wann",
    geb_where: "Wo",
    geb_art: "Gefahr",
    geb_hergang: "Hergang",
    geb_massnahmen: "Maßnahmen",
    geb_verursacher: "Verursacher",
    geb_abrechnung: "Abrechnung",
    geb_unterlagen: "Unterlagen",
    kfz_schaeden: "Betroffen",
    kfz_melder: "Melder",
    kfz_when: "Wann",
    kfz_where: "Wo",
    kfz_art: "Hergang",
    kfz_eigenes: "Eigenes Kfz",
    kfz_fremdes: "Fremdes Kfz",
    kfz_hergang: "Einschätzung",
    kfz_person: "Person",
    kfz_sachen: "Sachen",
    kfz_unterlagen: "Unterlagen",
    son_betroffen: "Betroffen",
    son_melder: "Melder",
    son_when: "Wann",
    son_where: "Wo",
    son_details: "Details",
    son_unterlagen: "Unterlagen",
    abschluss: "Abschluss",
    summary: "Prüfen",
    done: "Fertig",
  } as Record<string, string>,

  // ---- Step 1: type --------------------------------------------------------
  type: {
    eyebrow: "Schadenmeldung",
    title: "Welche Art von Schaden möchten Sie melden?",
    hint: "Wählen Sie eine Kategorie — wir passen die nächsten Schritte daran an.",
    options: {
      gebaeude: { label: "Gebäude", helper: "Haus, Wohnung, Gewerbeobjekt …" },
      kfz: { label: "KFZ", helper: "Auto, Lkw, Transporter, Anhänger, Motorrad …" },
      sonstiges: {
        label: "Sonstiges",
        helper: "Personen, Sachen, Vermögen, Haftpflicht …",
      },
    },
  },

  // ---- shared: Schadenmelder + Ansprechpartner -----------------------------
  melder: {
    eyebrow: "Kontaktdaten",
    title: "Wer meldet den Schaden?",
    hint: "Diese Angaben helfen uns, Sie zu erreichen und die Meldung zuzuordnen.",
    vorname: "Vorname",
    name: "Name",
    tel: "Telefonnummer",
    handy: "Handynummer",
    email: "E-Mail-Adresse",
    kuerzel: "Kürzel (optional)",
    fuerUnternehmen: "Für wen wird der Schaden gemeldet?",
    fuerUnternehmenPlaceholder: "Name des Unternehmens / Kunden",
    vorgangsnummer: "Interne Vorgangsnummer oder Schadennummer (falls vorhanden)",
    apTitle: "Mit wem sollen wir im weiteren Verlauf kommunizieren?",
    apOptions: [
      { value: "melder", label: "Mit dem Schadenmelder" },
      { value: "andere", label: "Mit folgender Person" },
    ] as Option[],
    apFunktion: "Funktion",
  },

  // ---- shared: Schadentag --------------------------------------------------
  when: {
    eyebrow: "Zeitpunkt",
    title: "Wann ist der Schaden passiert oder entdeckt worden?",
    hint: "Datum und Uhrzeit so genau Sie wissen.",
    chips: {
      now: "Gerade eben",
      today: "Heute",
      yesterday: "Gestern",
      week: "Diese Woche",
    },
    datum: "Schadendatum & Uhrzeit",
    zeitpunkt: "Falls Uhrzeit unbekannt: ungefährer Zeitpunkt",
    entdeckt: "Falls nicht bekannt: Wann wurde der Schaden entdeckt?",
  },

  // ===========================================================================
  // GEBÄUDE
  // ===========================================================================
  gebWhere: {
    eyebrow: "Ort des Schadens",
    title: "Wo ist der Schaden passiert?",
    hint: "Anschrift des geschädigten Objektes und betroffene Einheit.",
    plz: "PLZ",
    ort: "Ort",
    strasse: "Straße",
    objektNr: "Objekt-Nr. / Objektbezeichnung",
    wohneinheit: "Wohnung / Einheit-Nr. (falls vergeben)",
    sonstigeAngaben: "Sonstige Angaben zur Einheit",
    einheitTitle: "Welche Einheit ist vom Schaden betroffen?",
    einheitHint:
      "Wählen Sie die betroffene(n) Etage(n) und geben Sie Lage und Vermietung an (Beispiel: OG3 Mitte Vorne).",
    etagen: [
      { value: "keller", label: "Keller" },
      { value: "eg", label: "Erdgeschoss" },
      { value: "og1", label: "1. OG" },
      { value: "og2", label: "2. OG" },
      { value: "og3", label: "3. OG" },
      { value: "og4", label: "4. OG" },
      { value: "dach", label: "Dach" },
      { value: "sonstige", label: "Sonstige" },
    ] as Option[],
    lageLabel: "Lage",
    lageOptions: [
      { value: "links", label: "Links" },
      { value: "mitte", label: "Mitte" },
      { value: "rechts", label: "Rechts" },
      { value: "vorne", label: "Vorne" },
      { value: "hinten", label: "Hinten" },
    ] as Option[],
    vermietetLabel: "Vermietet?",
    zusatzLabel: "Zusatzangaben",
    mieterTitle: "Daten des Mieters der betroffenen Einheit",
    mieterInfo:
      "Daten des Mieters sind wegen des „Teilungsabkommens“ erforderlich. Gerade bei der Hausratversicherung des Mieters kann eine Doppelversicherung bestehen: Bei manchen Beschädigungen können die Kosten zwischen Gebäude- und Hausratversicherer geteilt werden, um die Schadenquote nicht komplett zu belasten. Beispiel: Bei einem Wasserschaden sind Boden und Tapete im 1. OG bei Mieter Schmitz beschädigt. Hat Familie Schmitz die Tapete selbst eingebracht, ersetzt deren Hausratversicherung diese Kosten.",
    mieterName: "Name des Mieters oder Nutzers",
    mieterTel: "Telefonnummer",
    mieterEmail: "E-Mail-Adresse",
    mieterHausrat:
      "Hat der Mieter oder Nutzer eine Hausrat- oder Inhaltsversicherung?",
    mieterVersicherer: "Falls bekannt: Name des Versicherers",
    mieterVsnr: "Versicherungsscheinnummer (falls bekannt)",
    beschaedigtLabel:
      "Wurden folgende Sachen beschädigt? (wichtig für das Teilungsabkommen)",
    beschaedigtOptions: [
      { value: "antennen", label: "Schäden an Einzelantennen" },
      { value: "markisen", label: "Schäden an Markisen" },
      { value: "verglasung", label: "Schäden an Verglasung" },
      { value: "tapeten", label: "Schäden an Tapeten oder Böden" },
      { value: "sonstiges", label: "Sonstiges" },
    ] as Option[],
  },

  gebArt: {
    eyebrow: "Schadenart",
    title: "Welche Art von Schaden liegt vor?",
    hint: "Wählen Sie die passende Gefahr.",
    info: "Weitere Elementargefahren sind z. B. Überschwemmung, Rückstau, Erdbeben, Erdsenkung, Erdrutsch, Schneedruck, Lawinen oder Vulkanausbruch.",
    options: [
      { value: "feuer", label: "Feuer" },
      { value: "leitungswasser", label: "Leitungswasser" },
      { value: "sturm_hagel", label: "Sturm / Hagel" },
      { value: "sonstige", label: "Sonstige (alle anderen)" },
    ] as Option[],
    // Zweite Ebene ("Untergefahren") je gewählter Gefahr (Stand 2026-05-27).
    untergefahrLabel: "Was trifft genauer zu?",
    untergefahrDetail: "Bitte kurz beschreiben",
    untergefahren: {
      feuer: [
        { value: "explosion", label: "Explosion" },
        { value: "brandstiftung", label: "Brandstiftung" },
        { value: "blitz", label: "Blitz (Überspannung)" },
        { value: "sonstiges", label: "Sonstiges" },
      ],
      leitungswasser: [
        { value: "rohrbruch_abwasser_innen", label: "Rohrbruch – Abwasserleitung innenliegend" },
        { value: "rohrbruch_abwasser_aussen", label: "Rohrbruch – Abwasserleitung außenliegend (Kanal)" },
        { value: "rohrbruch_zuleitung_innen", label: "Rohrbruch – Zuleitung innenliegend" },
        { value: "rohrbruch_zuleitung_aussen", label: "Rohrbruch – Zuleitung außenliegend" },
        { value: "rohrbruch_sonstiges", label: "Rohrbruch – Sonstiges" },
        { value: "verstopfung_innen", label: "Verstopfung – innenliegend" },
        { value: "verstopfung_aussen", label: "Verstopfung – außenliegend" },
        { value: "armaturen", label: "Sonstige Ursache – Armaturen" },
        { value: "undichte_fugen", label: "Sonstige Ursache – Undichte Fugen" },
        { value: "schlaeuche", label: "Sonstige Ursache – Schläuche / Verbindungen" },
        { value: "sonstiges", label: "Sonstige Ursache – Sonstiges" },
      ],
      sturm_hagel: [
        { value: "dach_spitz", label: "Dach – Spitzdach" },
        { value: "dach_flach", label: "Dach – Flachdach" },
        { value: "kamin", label: "Kamin" },
        { value: "baum", label: "Baum" },
        { value: "sonstiges", label: "Sonstiges" },
      ],
      sonstige: [
        { value: "ueberschwemmung", label: "Überschwemmung" },
        { value: "rueckstau", label: "Rückstau" },
        { value: "starkregen", label: "Starkregen" },
        { value: "schneedruck", label: "Schneedruck" },
        { value: "erdbeben", label: "Erdbeben" },
        { value: "erdrutsch", label: "Erdrutsch / Erdsenkung" },
        { value: "vulkan", label: "Vulkanausbruch" },
        { value: "glas_wohnung", label: "Glasbruch – Verglasung Wohnungen" },
        { value: "glas_allgemein", label: "Glasbruch – Allgemeine Verglasung (Treppenhaus etc.)" },
        { value: "glas_gewerbe", label: "Glasbruch – Verglasung Gewerberaum (ohne Schaufenster)" },
        { value: "glas_schaufenster", label: "Glasbruch – Schaufensterverglasung" },
        { value: "graffiti", label: "Graffiti" },
        { value: "gebaeude_dritte", label: "Gebäudebeschädigung durch unbefugte Dritte" },
        { value: "innere_unruhen", label: "Innere Unruhen" },
        { value: "boeswillig", label: "Böswillige Beschädigung" },
        { value: "streik", label: "Streik" },
        { value: "fahrzeuganprall", label: "Fahrzeuganprall" },
        { value: "rauch", label: "Rauch" },
        { value: "ueberschalldruck", label: "Überschalldruck" },
        { value: "sonstiges", label: "Sonstiges" },
      ],
    } as Record<string, Option[]>,
  },

  gebHergang: {
    eyebrow: "Schadenhergang",
    title: "Bitte beschreiben Sie, was passiert ist",
    hint: "Je genauer, desto schneller können wir helfen.",
    hergang: "Was ist passiert?",
    schadenstelle: "Wo genau ist die Schadenstelle?",
    ursacheLabel: "Ist die Ursache bekannt oder muss sie noch gesucht werden?",
    ursacheOptions: [
      { value: "bekannt", label: "Ursache ist bekannt" },
      { value: "suchen", label: "Ursache muss noch gesucht werden" },
    ] as Option[],
    folgeschaeden: "Sind Folgeschäden zu erwarten?",
    umfangTitle: "Schadenumfang und Schadenhöhe",
    betroffeneTeile:
      "Welche Gebäudeteile, Räume oder Einrichtungen sind beschädigt?",
    schadenhoehe: "Geschätzte Schadenhöhe in EUR",
    hoeheInfo:
      "Bedingungsgemäß steht dem Versicherungsnehmer ausschließlich der gleichwertige Ersatz im Schadenfall zu. Verbesserungen, Wertsteigerungen oder Mehrleistungen können durch den Versicherer nicht entschädigt werden.",
    nutzung:
      "Können Räume, Wohnungen oder Gewerbeeinheiten noch genutzt werden?",
    nutzungDetail: "Bitte beschreiben Sie kurz, was nicht mehr genutzt werden kann.",
  },

  gebMassnahmen: {
    eyebrow: "Maßnahmen & Sanierung",
    title: "Wurde bereits etwas unternommen?",
    hint: "Beauftragte Maßnahmen, Kosten und geplante Sanierungen.",
    beauftragt: "Wurde bereits eine Maßnahme beauftragt?",
    was: "Was genau wurde unternommen?",
    firma: "Welche Firma wurde beauftragt?",
    auftragsnummer: "Gibt es eine Auftragsnummer?",
    kostenvoranschlag: "Gibt es bereits einen Kostenvoranschlag?",
    rechnungen: "Gibt es bereits Rechnungen?",
    sanierungTitle: "Reparatur, Sanierung oder Verbesserung",
    sanierungLabel:
      "Werden im Zuge der Schadenbehebung sonstige Sanierungsmaßnahmen vorgenommen?",
    sanierungInfo:
      "Diese Frage stellen wir, weil solche Arbeiten nicht schadenbedingt notwendig sind. Diese Kosten sind nicht versicherbar und müssen in der Schadenabrechnung sauber getrennt werden. Gleiches gilt für Verbesserungsmaßnahmen. Beispiel: Beim Rohrbruch sind 5 Meter schadenbedingte Kosten, aber das ganze Rohr (10 Meter) ist beschädigt — die restlichen 5 Meter wären eine Sanierung aus Eigeninteresse.",
  },

  gebVerursacher: {
    eyebrow: "Beteiligte",
    title: "Möglicher Verursacher und weitere Beteiligte",
    hint: "Verursacher, polizeiliche Meldung und weitere Geschädigte.",
    label: "Ist bekannt, wer den Schaden verursacht haben könnte?",
    info: "Diese Daten fragen wir an, da wir bei bekannten Verursachern Regress nehmen können: Wir begleichen den Schaden vorab und holen uns die Summe beim Versicherer des Verursachers zurück, sodass Ihre Schadenquote nicht komplett belastet wird. Beispiel: Der Mieter hat eine Waschmaschine angeschlossen, der Schlauch ist undicht und Wasser dringt aus. Hat der Mieter eine Privathaftpflichtversicherung, übernehmen wir vorab die Leistung und nehmen dann Regress.",
    name: "Name",
    tel: "Telefonnummer",
    email: "E-Mail-Adresse",
    haftpflicht: "Hat die Person eine Haftpflichtversicherung?",
    versicherer: "Name des Versicherers",
    vsnr: "Versicherungsnummer",
    polizeiTitle: "Polizeiliche Meldung",
    polizeiInfo:
      "Eine polizeiliche Meldung wird nur in bestimmten Fällen benötigt, z. B. bei Gebäudebeschädigung (etwa versuchter Einbruch an der Wohnungs- oder Hauseingangstür) sowie bei Einbruch- bzw. Einbruchdiebstahlschäden.",
    polizeiLabel: "Wurde der Schaden polizeilich gemeldet?",
    polizeiOptions: [
      { value: "nein", label: "Nein" },
      { value: "ja", label: "Ja" },
      { value: "unbekannt", label: "Ich weiß es nicht" },
    ] as Option[],
    aktenzeichen: "Aktenzeichen",
    dienststelle: "Dienststelle",
    weitereTitle: "Weitere Geschädigte",
    weitereLabel: "Gibt es weitere Geschädigte (z. B. Nachbarn)?",
    weitereArt: "Bitte erläutern Sie kurz, um welche Art von Schaden es sich handelt",
  },

  gebAbrechnung: {
    eyebrow: "Abrechnung",
    title: "Steuer, Zahlung und Hinweise",
    hint: "Vorsteuerabzug, Schadenzahlung und sonstige Hinweise.",
    vorsteuerLabel:
      "Ist der Versicherungsnehmer bzw. Eigentümer für diesen Schaden vorsteuerabzugsberechtigt?",
    vorsteuerOptions: [
      { value: "nein", label: "Nein" },
      { value: "ja", label: "Ja, Anteil in %" },
    ] as Option[],
    vorsteuerAnteil: "Anteil in %",
    zahlungLabel: "An wen soll die Schadenzahlung erfolgen?",
    zahlungOptions: [
      { value: "firma", label: "An uns" },
      { value: "sonstige", label: "Sonstige" },
    ] as Option[],
    zahlungName: "Angaben zum Zahlungsempfänger",
    iban: "IBAN",
    bic: "BIC",
    zusatzTitle: "Zusätzliche Informationen",
    zusatzLabel: "Möchten Sie uns zusätzlich noch etwas mitteilen?",
    zusatzPlaceholder: "Freitextfeld für sonstige Hinweise",
  },

  // ===========================================================================
  // KFZ
  // ===========================================================================
  kfzSchaeden: {
    eyebrow: "Schadenmeldung KFZ",
    title: "Welche Schäden sind entstanden?",
    hint: "Mehrfachauswahl möglich.",
    options: [
      { value: "eigenes", label: "Schaden am eigenen Fahrzeug" },
      { value: "fremdes", label: "Schaden am fremden Fahrzeug" },
      { value: "person", label: "Personenschaden" },
      { value: "sonstige", label: "Schaden an sonstigen Sachen oder Gegenständen" },
    ] as Option[],
  },

  kfzWhere: {
    eyebrow: "Ort des Schadens",
    title: "Wo ist der Schaden passiert?",
    hint: "Bitte so genau wie möglich.",
    ortLabel: "Ort des Schadens",
    ortPlaceholder:
      "z. B. Straße, Hausnummer, PLZ, Ort, Parkplatz, Hof, Garage, Betriebsgelände — bei Autobahnen: Autobahnnummer, Fahrtrichtung, Kilometerangabe, Anschlussstelle, Rastplatz.",
  },

  kfzArt: {
    eyebrow: "Schadenhergang",
    title: "Was ist grob passiert?",
    hint: "Bitte wählen Sie die passendste Beschreibung aus.",
    options: [
      { value: "unfall_fremd", label: "Unfall oder Zusammenstoß mit einem anderen Fahrzeug" },
      { value: "ohne_fremd", label: "Schaden ohne Mitwirkung eines anderen Fahrzeugs" },
      { value: "parkschaden", label: "Parkschaden" },
      { value: "unbekannt", label: "Unbekannter Verursacher" },
      { value: "glasbruch", label: "Glasbruch" },
      { value: "wild", label: "Schaden durch Wild, Marder oder Tierbiss" },
      { value: "diebstahl", label: "Diebstahl, Einbruch oder Vandalismus" },
      { value: "sturm_hagel", label: "Sturm, Hagel" },
      { value: "natur", label: "Naturereignis (Überschwemmung, Starkregen u. a.)" },
      { value: "brand", label: "Brand, Explosion" },
      { value: "bekannte_person", label: "Beschädigung durch andere bekannte Person/Ursache" },
      { value: "sonstiges", label: "Sonstiges" },
    ] as Option[],
    unfallLabel: "Was beschreibt den Unfall am besten?",
    unfallOptions: [
      { value: "auffahr", label: "Auffahrunfall" },
      { value: "frontal_seitlich", label: "Frontaler oder seitlicher Zusammenstoß" },
      { value: "spurwechsel", label: "Spurwechsel" },
      { value: "abbiegen", label: "Abbiegen oder Vorfahrt" },
      { value: "rangieren", label: "Rangieren" },
      { value: "anfahren", label: "Anfahren" },
      { value: "ausparken", label: "Ausparken" },
      { value: "rueckwaerts", label: "Rückwärtsfahren" },
      { value: "sonstiges", label: "Sonstiges" },
    ] as Option[],
    ohneFremdLabel: "Was beschreibt den Schaden am besten?",
    ohneFremdOptions: [
      { value: "hindernis", label: "Gegen Pfosten, Mauer, Tor, Leitplanke oder anderes Hindernis gefahren" },
      { value: "umgekippt", label: "Fahrzeug umgekippt, abgerutscht oder von der Fahrbahn abgekommen" },
      { value: "rangieren", label: "Schaden beim Rangieren, Anfahren, Ausparken oder Rückwärtsfahren ohne anderes Fahrzeug" },
      { value: "sonstiges", label: "Sonstiges" },
    ] as Option[],
  },

  kfzEigenes: {
    eyebrow: "Eigenes Fahrzeug",
    title: "Welches eigene Fahrzeug ist betroffen?",
    hint: "Fahrzeugdaten, Fahrer und sichtbare Schäden.",
    kennzeichen: "Kennzeichen",
    art: "Fahrzeugart (z. B. Pkw, Lkw, Transporter, Anhänger, Motorrad)",
    km: "Kilometerstand",
    anhaenger: "War ein Anhänger beteiligt?",
    anhaengerKz: "Kennzeichen des Anhängers",
    fahrerTitle: "Wer hat das eigene Fahrzeug zum Schadenzeitpunkt gefahren?",
    fahrerName: "Name, Vorname",
    fahrerTel: "Telefonnummer",
    fahrerEmail: "E-Mail-Adresse",
    schaedenTitle: "Welche Schäden sind an unserem Fahrzeug sichtbar?",
    zonenHint: "Tippen Sie auf die betroffenen Fahrzeugbereiche.",
    abgeschleppt: "Musste das Fahrzeug abgeschleppt oder gesichert werden?",
    hoehe: "Geschätzte Schadenhöhe in EUR",
    werkstatt: "Wurde bereits eine Werkstatt eingeschaltet?",
    gutachter: "Wurde bereits ein Gutachter eingeschaltet?",
  },

  kfzFremdes: {
    eyebrow: "Fremdes Fahrzeug / Unfallgegner",
    title: "Welches fremde Fahrzeug ist betroffen?",
    hint: "Fahrzeug, Fahrer, Halter, Versicherer und Schäden.",
    kennzeichen: "Kennzeichen",
    art: "Fahrzeugart (z. B. Pkw, Lkw, Transporter, Anhänger, Motorrad)",
    hersteller: "Hersteller / Modell (soweit bekannt)",
    farbe: "Farbe (soweit bekannt)",
    anhaenger: "Anhänger beteiligt?",
    fahrbereit: "Ist das fremde Fahrzeug noch fahrbereit?",
    fahrerTitle: "Wer war Fahrer des fremden Fahrzeugs?",
    fahrerName: "Name, Vorname",
    fahrerAdresse: "Straße, PLZ, Ort",
    fahrerTel: "Telefonnummer",
    fahrerEmail: "E-Mail-Adresse (falls bekannt)",
    halterTitle: "Wer ist Halter oder Eigentümer des fremden Fahrzeugs?",
    halterName: "Name / Firma",
    halterAdresse: "Straße, PLZ, Ort",
    halterTel: "Telefonnummer",
    halterEmail: "E-Mail-Adresse (falls bekannt)",
    versichererBekannt: "Ist der Versicherer des fremden Fahrzeugs bekannt?",
    versicherer: "Name des Versicherers",
    vsnr: "Versicherungsscheinnummer (falls bekannt)",
    schadennr: "Schadennummer (falls bereits vorhanden)",
    ansprech: "Ansprechpartner beim Versicherer (falls bekannt)",
    vorsteuer:
      "Ist der Halter oder Eigentümer des fremden Fahrzeugs vorsteuerabzugsberechtigt?",
    schaedenTitle: "Welche Schäden am fremden Fahrzeug sind bekannt?",
    zonenHint: "Tippen Sie auf die betroffenen Fahrzeugbereiche.",
    abgeschleppt: "Musste das fremde Fahrzeug abgeschleppt werden?",
    hoehe: "Geschätzte Schadenhöhe in EUR",
    kv: "Gibt es bereits Kostenvoranschlag, Rechnung oder Gutachten?",
    kvHint: "Diese Unterlagen können Sie am Ende der Schadenmeldung hochladen.",
    mietwagen: "Wird Mietwagen, Nutzungsausfall oder sonstiger Ersatz verlangt?",
    mietwagenDetail:
      "Bitte erläutern Sie kurz, was benötigt oder geltend gemacht wird.",
    weiteresFahrzeug: "Weiteres fremdes betroffenes Fahrzeug",
  },

  kfzHergang: {
    eyebrow: "Einschätzung",
    title: "Wie schätzen Sie den Hergang aktuell ein?",
    hint: "Diese Einschätzung ist kein Schuldanerkenntnis. Sie hilft uns nur bei der ersten Einordnung.",
    einschaetzungOptions: [
      { value: "wir", label: "Wir haben den Schaden vermutlich verursacht" },
      { value: "gegner", label: "Die andere Person / das andere Fahrzeug hat den Schaden vermutlich verursacht" },
      { value: "beide", label: "Beide Seiten könnten beteiligt sein" },
      { value: "unbekannt", label: "Der Verursacher ist unbekannt" },
      { value: "keine", label: "Ich kann das nicht beurteilen" },
    ] as Option[],
    zeugenTitle: "Gibt es Zeugen?",
    zeugenAngaben: "Name, Anschrift, Telefonnummer",
    zeugenAussage: "Was kann die Person bezeugen?",
    skizzeTitle: "Gibt es eine Unfallskizze oder einen Unfallbericht?",
    skizzeOptions: [
      { value: "europ", label: "Europäischer Unfallbericht vorhanden" },
      { value: "eigene", label: "Eigene Unfallskizze vorhanden" },
      { value: "keine", label: "Keine Skizze vorhanden" },
      { value: "nachreichen", label: "Wird nachgereicht" },
    ] as Option[],
  },

  kfzPerson: {
    eyebrow: "Personenschaden",
    title: "Welche Person wurde verletzt?",
    hint: "Mehrere Personen möglich — bitte die wichtigste zuerst.",
    name: "Wer wurde verletzt? (Vorname, Name)",
    rolleLabel: "Wer war die betroffene Person in der Situation?",
    rolleOptions: [
      { value: "fahrer", label: "Fahrer" },
      { value: "beifahrer", label: "Beifahrer" },
      { value: "insasse", label: "Insasse" },
      { value: "sonstiges", label: "Sonstiges" },
    ] as Option[],
    rolleDetail:
      "Bitte beschreiben (z. B. Fußgänger, Fahrradfahrer, Motorradfahrer)",
    zugehoerigLabel: "Gehört die betroffene Person zu Ihrem Unternehmen?",
    zugehoerigOptions: [
      { value: "ja", label: "Ja, die Person gehört zu unserem Unternehmen" },
      { value: "nein", label: "Nein, es handelt sich um eine dritte / fremde Person" },
      { value: "unbekannt", label: "Ich weiß es nicht" },
    ] as Option[],
    verletzung: "Welche Verletzung ist bekannt?",
    arbeitsunfaehig: "Ist die Person arbeitsunfähig?",
    unterlagen: "Gibt es ärztliche Unterlagen?",
    weitereLabel: "Gibt es weitere verletzte Personen?",
  },

  kfzSachen: {
    eyebrow: "Sonstige Sachen",
    title: "Welche sonstigen Sachen oder Gegenstände wurden beschädigt?",
    hint: "Bitte beschreiben Sie die beschädigten Sachen.",
    beschreibung: "Beschreibung der beschädigten Sachen oder Gegenstände",
    eigentuemerLabel: "Wem gehören diese Sachen oder Gegenstände?",
    eigentuemerOptions: [
      { value: "uns", label: "Uns selbst" },
      { value: "fremd", label: "Einer fremden Person / Firma" },
      { value: "unbekannt", label: "Unbekannt" },
    ] as Option[],
    fremdAngaben: "Name, Anschrift, Telefonnummer, E-Mail-Adresse",
    hoehe: "Geschätzte Schadenhöhe in EUR",
    hergang: "Wie wurden die Sachen beschädigt? Bitte schildern Sie den Hergang.",
    belegeLabel: "Gibt es Fotos, Kostenvoranschläge oder Rechnungen?",
    belegeOptions: [
      { value: "fotos", label: "Fotos vorhanden" },
      { value: "kv", label: "Kostenvoranschlag vorhanden" },
      { value: "rechnung", label: "Rechnung vorhanden" },
      { value: "nachreichen", label: "Wird nachgereicht" },
      { value: "nein", label: "Nein" },
    ] as Option[],
    vorsteuer:
      "Ist der Besitzer oder Eigentümer der Sachen vorsteuerabzugsberechtigt?",
  },

  // ===========================================================================
  // SONSTIGES
  // ===========================================================================
  sonBetroffen: {
    eyebrow: "Schadenmeldung Sonstiges",
    title: "Wer oder was ist vom Schaden betroffen?",
    hint: "Wählen Sie die passende Kategorie.",
    options: {
      mensch: { label: "Mensch", helper: "Eine Person ist betroffen" },
      sache: { label: "Sache", helper: "Gegenstände, Einrichtung, Waren …" },
      sonstiges: { label: "Sonstiges", helper: "Vermögen, Frist, Vertrag …" },
    },
  },

  sonWhere: {
    eyebrow: "Ort des Schadens",
    title: "Wo ist der Schaden passiert?",
    hint: "Anschrift oder Ortsbeschreibung.",
    plz: "PLZ",
    ort: "Ort",
    strasse: "Straße",
  },

  sonSache: {
    title: "Angaben zur beschädigten Sache",
    beschreibung: "Welche Sache oder welcher Gegenstand wurde beschädigt?",
    beschreibungHint:
      "Beispiele: Einrichtung, Maschinen, Geräte, Waren, Werkzeuge oder sonstige Gegenstände.",
    eigentuemerLabel: "Wem gehört die beschädigte Sache?",
    eigentuemerOptions: [
      { value: "uns", label: "Uns / unserem Unternehmen" },
      { value: "fremd", label: "Einer anderen Person / Firma" },
    ] as Option[],
    fremdAngaben: "Name / Firma, Anschrift, Telefonnummer, E-Mail-Adresse",
    ort: "Wo befindet sich die beschädigte Sache?",
    hergang: "Wie wurde die Sache beschädigt? (kurze Schilderung)",
    hoehe: "Geschätzte Schadenhöhe in EUR",
    zeugen: "Gibt es Zeugen oder weitere Beteiligte?",
    zeugenAngaben: "Name, Kontaktdaten",
    belegeLabel: "Gibt es Fotos, Kostenvoranschläge oder Rechnungen?",
    belegeOptions: [
      { value: "fotos", label: "Fotos vorhanden" },
      { value: "kv", label: "Kostenvoranschlag vorhanden" },
      { value: "rechnung", label: "Rechnung vorhanden" },
      { value: "nachreichen", label: "Wird nachgereicht" },
      { value: "nein", label: "Nein" },
    ] as Option[],
  },

  sonMensch: {
    title: "Angaben zur betroffenen Person",
    werLabel: "Gehört die betroffene Person zu Ihrem Unternehmen?",
    werOptions: [
      { value: "ja", label: "Ja, die Person gehört zu unserem Unternehmen" },
      { value: "nein", label: "Nein, es handelt sich um eine dritte / fremde Person" },
      { value: "unbekannt", label: "Ich weiß es nicht" },
    ] as Option[],
    rolle:
      "Beschreiben Sie kurz, welche Rolle die dritte / fremde Person im Schadenfall hatte (z. B. Kunde, Besucher, Mieter, Passant).",
    hergang: "Was ist passiert? (kurze Schilderung)",
    verletzung: "Welche Verletzung oder Beeinträchtigung ist bekannt?",
    arbeitsunfaehig: "Ist die Person arbeitsunfähig?",
    weitereLabel: "Gibt es weitere verletzte Personen?",
    zeugen: "Gibt es Zeugen oder weitere Beteiligte?",
  },

  sonSonst: {
    title: "Was ist passiert?",
    hergang: "Bitte genaue Schilderung",
    hergangHint:
      "Beispiele: Vermögensschaden, Datenverlust, Schlüsselverlust, Fristversäumnis, Mietausfall, Vertragsproblem oder ein anderer Vorfall.",
    werLabel: "Wer ist betroffen?",
    werOptions: [
      { value: "uns", label: "Wir selbst" },
      { value: "kunde", label: "Kunden" },
      { value: "fremd", label: "Fremde Person" },
    ] as Option[],
    betroffenAngaben: "Angaben zum Betroffenen (Name, Anschrift, Telefon, E-Mail)",
    hoehe: "Geschätzte Schadenhöhe in EUR",
    anspruch: "Wird bereits ein Anspruch gegen uns erhoben?",
    zeugen: "Gibt es Zeugen oder weitere Beteiligte?",
    zeugenAngaben: "Name, Kontaktdaten",
  },

  // ---- Unterlagen ----------------------------------------------------------
  unterlagen: {
    eyebrow: "Unterlagen",
    title: "Bitte laden Sie folgende Unterlagen hoch",
    hint: "Keine Sorge: Bitte nur hochladen, was bereits vorhanden ist. Fehlende Unterlagen können später nachgereicht werden.",
    note: "Markieren Sie, welche Unterlagen Sie bereits haben — den Upload-Link senden wir Ihnen anschließend zu.",
    fotosHint:
      "Hilfreich sind: Übersicht des Raums, Nahaufnahme der Schadenstelle, Ursache/Austrittsstelle (falls sichtbar), betroffene Wand/Decke/Boden, beschädigte Sachen.",
    gebOptions: [
      { value: "fotos", label: "Fotos oder Video vom Schaden" },
      { value: "kv", label: "Kostenvoranschlag (falls vorhanden)" },
      { value: "rechnung", label: "Rechnung (falls vorhanden)" },
      { value: "schriftverkehr", label: "Schriftverkehr mit Mietern, Hausverwaltung, Handwerkern oder Dritten" },
      { value: "sonstige", label: "Sonstige vorhandene Unterlagen" },
    ] as Option[],
    kfzOptions: [
      { value: "fotos_eigen", label: "Fotos / Video vom eigenen Fahrzeug" },
      { value: "fotos_fremd", label: "Fotos / Video vom fremden Fahrzeug" },
      { value: "fotos_ort", label: "Fotos / Video vom Schaden- / Unfallort" },
      { value: "fotos_sachen", label: "Fotos / Video von beschädigten Sachen u. Gegenständen" },
      { value: "europ_bericht", label: "Europäischer Unfallbericht" },
      { value: "skizze", label: "Unfallskizze" },
      { value: "polizei", label: "Polizeibericht oder Aktenzeichen (falls vorhanden)" },
      { value: "kv", label: "Kostenvoranschlag" },
      { value: "rep_rechnung", label: "Reparaturrechnung" },
      { value: "abschlepp", label: "Abschlepprechnung" },
      { value: "gutachten", label: "Gutachten" },
      { value: "schreiben", label: "Schreiben des fremden Fahrers, Halters, Versicherers oder Anwalts" },
      { value: "versicherer", label: "Versichererdaten des fremden Fahrzeugs" },
      { value: "sonstige", label: "Sonstige Unterlagen" },
    ] as Option[],
    sonOptions: [
      { value: "fotos_sachen", label: "Fotos / Video von beschädigten Sachen u. Gegenständen" },
      { value: "fotos_ort", label: "Fotos / Video vom Schadenort" },
      { value: "polizei", label: "Polizeibericht oder Aktenzeichen (falls vorhanden)" },
      { value: "kv", label: "Kostenvoranschlag" },
      { value: "rep_rechnung", label: "Reparaturrechnung" },
      { value: "sonstige", label: "Sonstige Unterlagen" },
    ] as Option[],
    // KFZ-Unterlagen nur zeigen, wenn die jeweilige Schadenkategorie gewählt
    // wurde (Mehrfach-„oder"). Fehlt ein Eintrag, wird die Option immer gezeigt.
    kfzOptionRequires: {
      fotos_eigen: ["eigenes"],
      fotos_fremd: ["fremdes"],
      fotos_sachen: ["sonstige"],
      europ_bericht: ["fremdes"],
      rep_rechnung: ["eigenes", "fremdes"],
      abschlepp: ["eigenes", "fremdes"],
      gutachten: ["eigenes", "fremdes"],
      schreiben: ["fremdes"],
      versicherer: ["fremdes"],
    } as Record<string, string[]>,
    // zusätzlich je Gebäude-Schadenart sinnvolle Unterlagen
    gebExtra: {
      leitungswasser:
        "Leckortungsbericht, Installateurbericht, Trocknungsprotokoll, Feuchtigkeitsmessung, Fotos der Austrittsstelle, Rechnung Rohr-/Leitungsreparatur.",
      feuer:
        "Feuerwehrbericht, Brandursachenermittlung, Elektrikerbericht bei Überspannung, Fotos von Rauch-/Rußschäden, ggf. polizeiliche Meldung bei Brandstiftung.",
      sturm_hagel:
        "Wetter- oder Sturmnachweis, Dachdeckerbericht, Fotos von Außen-/Dachschäden, Nachweis Notabdichtung.",
      sonstige:
        "Je nach Ereignis: Wetternachweis, Rohrreinigungsbericht, Fotos der Verglasung, Zeugendaten, Fachbericht/Prüfbericht oder ggf. polizeiliche Meldung.",
    } as Record<string, string>,
  },

  // ---- Abschluss (Kopie + Einwilligung) ------------------------------------
  abschluss: {
    eyebrow: "Abschluss",
    title: "Fast geschafft",
    hint: "Wie möchten Sie eine Kopie Ihrer Meldung erhalten?",
    kopieLabel: "Kopie der Meldung",
    kopieOptions: [
      { value: "mail", label: "Per E-Mail" },
      { value: "download", label: "Als Download" },
      { value: "beides", label: "Beides" },
    ] as Option[],
    consent:
      "Ich willige in die Verarbeitung meiner Angaben zur Bearbeitung dieser Schadenmeldung ein.",
  },

  // ---- Summary / Done ------------------------------------------------------
  summary: {
    eyebrow: "Prüfen & senden",
    title: "Alles korrekt?",
    hint: "Bitte prüfen Sie Ihre Angaben — Sie können einzelne Schritte noch ändern.",
  },
  done: {
    title: "Danke — wir haben Ihre Meldung erhalten.",
    body: "Ein Mitarbeiter prüft Ihre Schadenmeldung und meldet sich innerhalb eines Werktags bei Ihnen.",
    referenceLabel: "Ihre Vorgangs-Referenz",
    team: "Ihr Courté-Team",
    naechsteTitle: "Wie geht es weiter",
    naechste: [
      "Wir prüfen Ihre Angaben.",
      "Falls etwas fehlt, melden wir uns bei Ihnen.",
      "Bei dringenden Schäden nehmen wir kurzfristig Kontakt auf.",
      "Bitte bewahren Sie beschädigte Teile, Rechnungen, Angebote und Schriftverkehr auf.",
    ],
  },

  errors: {
    submit:
      "Leider konnte die Meldung nicht gesendet werden. Bitte versuchen Sie es erneut.",
  },
} as const;

// Wichtige Hinweise (allgemein) + Empfehlungen je Schadenart, gezeigt am Ende.
export interface HinweisBlock {
  title: string;
  items: string[];
}

export const GEB_HINWEISE: string[] = [
  "Bitte sorgen Sie zuerst dafür, dass keine Personen gefährdet werden. Treffen Sie nur notwendige Sofortmaßnahmen, um den Schaden nicht größer werden zu lassen (z. B. Absperren, Sichern, Abstellen der Wasserzufuhr oder Abdecken einer offenen Stelle), soweit dies gefahrlos möglich ist.",
  "Bitte dokumentieren Sie den Schaden möglichst vor Veränderungen mit Fotos oder Videos. Bewahren Sie beschädigte Teile, Rechnungen, Angebote und Schriftverkehr auf.",
  "Bitte erkennen Sie keine Ansprüche an und leisten Sie keine Zahlungen an Dritte, bevor der Schaden geprüft wurde.",
  "Bitte stimmen Sie größere Reparaturen, Sanierungen oder kostenintensive Maßnahmen vorher mit uns ab. Notwendige Sofortmaßnahmen zur Schadenminderung sollten nachvollziehbar dokumentiert werden.",
];

export const GEB_EMPFEHLUNGEN: HinweisBlock[] = [
  {
    title: "Bei Leitungswasser",
    items: [
      "Stellen Sie die Wasserzufuhr ab, soweit dies gefahrlos möglich ist.",
      "Sichern Sie betroffene Bereiche und vermeiden Sie weitere Nutzung, wenn dadurch der Schaden größer werden könnte.",
      "Dokumentieren Sie die Austrittsstelle, Feuchtigkeitsspuren und betroffene Wände, Böden oder Decken mit Fotos.",
      "Bei ungeklärter Ursache keine vorschnellen Öffnungs-, Abbruch- oder Sanierungsarbeiten — die Ursache lässt sich oft mit speziellen Suchmethoden eingrenzen (z. B. Feuchtigkeitsmessung, Druckprüfung, Thermografie, akustische Ortung, Kamerabefahrung).",
    ],
  },
  {
    title: "Bei Verstopfung oder Ablaufproblem",
    items: [
      "Nutzen Sie den betroffenen Abfluss, die Toilette, Dusche oder Leitung möglichst nicht weiter, wenn sich Wasser zurückstaut oder nicht richtig abläuft.",
      "Dokumentieren Sie, wo das Problem auftritt und ob Nässe oder Wasser ausgetreten ist.",
      "Notwendige Sofortmaßnahmen sollten nachvollziehbar dokumentiert werden.",
    ],
  },
  {
    title: "Bei Feuer, Rauch, Ruß, Explosion, Blitz oder Überspannung",
    items: [
      "Betreten Sie betroffene Bereiche nur, wenn keine Gefahr besteht.",
      "Lassen Sie elektrische Anlagen oder Geräte unberührt, wenn Schäden oder Sicherheitsrisiken erkennbar sind.",
      "Dokumentieren Sie Brand-, Rauch-, Ruß-, Geruchs- oder Überspannungsschäden mit Fotos, soweit dies gefahrlos möglich ist.",
    ],
  },
  {
    title: "Bei Sturm oder Hagel",
    items: [
      "Sichern Sie offene oder beschädigte Stellen, soweit dies gefahrlos möglich ist.",
      "Vermeiden Sie weitere Schäden durch eindringende Nässe, z. B. durch provisorisches Abdecken.",
      "Dokumentieren Sie Schäden an Dach, Fassade, Fenstern oder Außenbereichen mit Fotos.",
    ],
  },
  {
    title: "Bei Starkregen, Überschwemmung oder anderen Naturereignissen",
    items: [
      "Betreten Sie betroffene Bereiche nur, wenn keine Gefahr besteht.",
      "Sichern Sie wichtige Gegenstände, soweit dies gefahrlos möglich ist.",
      "Dokumentieren Sie Wasserstand, betroffene Räume, Eintrittsstellen und beschädigte Sachen mit Fotos oder Videos.",
      "Entfernen Sie beschädigte Sachen nur, wenn dies aus Sicherheits- oder Schadenminderungsgründen erforderlich ist.",
    ],
  },
  {
    title: "Bei Glasbruch",
    items: [
      "Vermeiden Sie Verletzungsgefahr durch Glasscherben.",
      "Sichern Sie die Bruchstelle, soweit dies gefahrlos möglich ist.",
      "Machen Sie Fotos vor der Reparatur oder Notverglasung. Bewahren Sie Rechnungen oder Angebote auf.",
    ],
  },
  {
    title: "Bei Beschädigung durch Dritte, Graffiti oder Fahrzeuganprall",
    items: [
      "Dokumentieren Sie die Beschädigung mit Fotos, bevor gereinigt oder repariert wird.",
      "Notieren Sie bekannte Verursacher, Zeugen oder Hinweise.",
      "Bewahren Sie vorhandene Schreiben, Fotos, Nachrichten oder sonstige Nachweise auf.",
      "Erkennen Sie keine Ansprüche an und leisten Sie keine Zahlung, bevor der Schaden geprüft wurde.",
    ],
  },
  {
    title: "Bei technischem Schaden an Gebäudetechnik",
    items: [
      "Nutzen Sie die betroffene Anlage nicht weiter, wenn dadurch weitere Schäden entstehen könnten.",
      "Notieren oder fotografieren Sie Fehlermeldungen, Störungen oder Warnanzeigen.",
      "Bewahren Sie Prüfberichte, Angebote, Rechnungen und Wartungsnachweise auf.",
    ],
  },
];

export const SON_HINWEISE: string[] = [
  "Bitte sorgen Sie zuerst dafür, dass keine Personen gefährdet werden, und treffen Sie nur notwendige Sofortmaßnahmen.",
  "Bitte dokumentieren Sie den Schaden möglichst vor Veränderungen mit Fotos oder Videos und bewahren Sie Belege, Schriftverkehr und betroffene Sachen auf.",
  "Bitte erkennen Sie keine Ansprüche an und leisten Sie keine Zahlungen an Dritte, bevor der Schaden geprüft wurde.",
  "Bitte stimmen Sie größere oder kostenintensive Maßnahmen vorher mit uns ab.",
];

export const KFZ_HINWEISE: string[] = [
  "Bitte achten Sie zuerst darauf, dass keine Personen gefährdet werden. Sichern Sie die Unfall- oder Schadenstelle nur, soweit dies gefahrlos möglich ist.",
  "Bitte dokumentieren Sie den Schaden möglichst vor Veränderungen mit Fotos oder Videos — insbesondere beschädigte Fahrzeugteile, Schadenort, Kennzeichen, Spuren und ggf. das fremde Fahrzeug.",
  "Bitte erkennen Sie keine Schuld an und leisten Sie keine Zahlung an Dritte, bevor der Schaden geprüft wurde. Eine sachliche Beschreibung des Hergangs ist ausreichend.",
  "Bitte stimmen Sie größere Reparaturen, Gutachten, Mietwagen oder sonstige kostenintensive Maßnahmen möglichst vorher mit uns ab.",
  "Notwendige Sofortmaßnahmen zur Schadenminderung (z. B. Sicherung, Abschleppen, Schutz vor weiteren Schäden) sollten nachvollziehbar dokumentiert werden.",
];

export const KFZ_EMPFEHLUNGEN: HinweisBlock[] = [
  {
    title: "Bei Unfall oder Zusammenstoß mit einem anderen Fahrzeug",
    items: [
      "Sichern Sie die Unfallstelle nur, soweit dies gefahrlos möglich ist.",
      "Dokumentieren Sie beide Fahrzeuge, Kennzeichen, Schadenstellen, Unfallort und Fahrtrichtung mit Fotos.",
      "Notieren Sie Name, Anschrift, Telefonnummer, Kennzeichen und Versicherer des anderen Beteiligten.",
      "Beschreiben Sie den Hergang sachlich, ohne eine Schuld anzuerkennen.",
    ],
  },
  {
    title: "Bei Schaden ohne anderes Fahrzeug",
    items: [
      "Dokumentieren Sie die beschädigten Fahrzeugteile und den Schadenort mit Fotos.",
      "Wenn das Fahrzeug nicht mehr verkehrssicher ist, sollte es nicht weiter genutzt werden.",
      "Notwendige Sicherungs- oder Abschleppmaßnahmen sollten nachvollziehbar dokumentiert werden.",
    ],
  },
  {
    title: "Bei Parkschaden oder unbekanntem Verursacher",
    items: [
      "Fotografieren Sie den Schaden, den Standort des Fahrzeugs und die Umgebung.",
      "Notieren Sie, wann und wo das Fahrzeug abgestellt wurde und wann der Schaden entdeckt wurde.",
      "Prüfen Sie, ob es Zeugen, Hinweise, Kameras oder Nachrichten am Fahrzeug gibt.",
      "Verändern Sie die Schadenstelle möglichst erst nach ausreichender Dokumentation.",
    ],
  },
  {
    title: "Bei Diebstahl, Einbruch oder Vandalismus",
    items: [
      "Dokumentieren Sie beschädigte Schlösser, Scheiben, Innenraum, fehlende Teile und den Standort mit Fotos.",
      "Bewahren Sie vorhandene Schlüssel, Unterlagen, Nachrichten oder Hinweise auf.",
      "Verändern oder reinigen Sie das Fahrzeug möglichst erst nach ausreichender Dokumentation.",
      "Erkennen Sie keine Forderungen an und leisten Sie keine Zahlung an Dritte.",
    ],
  },
  {
    title: "Beschädigung durch eine andere bekannte Person oder Ursache",
    items: [
      "Dokumentieren Sie den Schaden und die mögliche Ursache mit Fotos.",
      "Notieren Sie Name, Anschrift und Kontaktdaten der beteiligten Person.",
      "Bewahren Sie Nachrichten, Schreiben oder sonstige Hinweise auf.",
      "Erkennen Sie keine Schuld an und treffen Sie keine Zahlungszusagen, bevor der Schaden geprüft wurde.",
    ],
  },
  {
    title: "Fremdes Fahrzeug wurde beschädigt",
    items: [
      "Dokumentieren Sie das fremde Fahrzeug, die Schadenstelle, das Kennzeichen und den Schadenort mit Fotos.",
      "Notieren Sie Fahrer, Halter, Versicherer und Kontaktdaten des anderen Fahrzeugs.",
      "Erkennen Sie keine Haftung an und leisten Sie keine Zahlung, bevor der Schaden geprüft wurde. Leiten Sie Schreiben, Forderungen oder Nachrichten bitte an uns weiter.",
    ],
  },
  {
    title: "Person wurde verletzt oder gesundheitlich betroffen",
    items: [
      "Bei Verletzungen steht die Versorgung der betroffenen Person im Vordergrund.",
      "Erfassen Sie, wer betroffen ist und welche Rolle die Person hatte (Fahrer, Beifahrer, Fußgänger, Radfahrer).",
      "Beschreiben Sie den Hergang sachlich und bewahren Sie ärztliche Unterlagen oder Bescheinigungen auf.",
    ],
  },
];
